import {
    ShoppingBag,
    Receipt,
    DollarSign,
    MessageCircle,
    Inbox,
    ThumbsUp,
    ThumbsDown,
    ChevronRightIcon,
    Users,
    Smartphone,
    Repeat,
} from "lucide-react";
import {
    Bar,
    BarChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "src/@/components/ui/card";
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "src/@/components/ui/chart";
import { MetricCard } from "#dashboard/components/insights/dashboard-metric-card";
import { useDashboardContext } from "#dashboard/hooks/use-dashboard-context";
import { ComparisonPeriod, Range } from "#dashboard/types";
import { useQuerySalesBigquery } from "#dashboard/hooks/use-query-sales-bigquery";
import { useQueryCustomers } from "#dashboard/hooks/use-query-customers";
import { useQuerySMSSubscribers } from "#dashboard/hooks/use-query-sms-subscribers";
import { getActiveStoreNumUnreads } from "src/redux/slices";
import { useQuerySalesCohorts } from "#dashboard/hooks/use-query-sales-cohorts";
import { DashboardGranularityPicker } from "../filters/granularity-picker";
import { QuickShortcutsCard } from "../quick-shortcuts-card";
import { ChartCard } from "./chart-card";
import { SectionHeader } from "./section-header";
import { SalesBreakdownCharts } from "./sales-breakdown-charts";
import { TopPromotionsChart } from "./top-promotions-chart";
import { TopItemsChart } from "./top-items-chart";
import { Routes } from "#navigation/routes";
import { Button } from "src/@/components/ui/button";
import { Skeleton } from "src/@/components/ui/skeleton";
import { CustomerInsightsMonthlyChart } from "./customer-insights-monthly-chart";
import { LoyaltyTiers } from "#dashboard/components/insights/loyalty-tiers";
import { useQueryCustomersSummary } from "#dashboard/hooks/use-query-customers-summary";

const getSalesChartConfig = (
    currentPeriodStartEnd: Range | null,
    comparisonPeriodStartEnd: Range | null,
) =>
    ({
        current: {
            label: `${currentPeriodStartEnd?.startDate?.format("MM/DD/YY")} - ${currentPeriodStartEnd?.endDate?.format("MM/DD/YY")}`,
            color: `hsl(var(--chart-1))`,
        },
        previous: {
            label: comparisonPeriodStartEnd
                ? `${comparisonPeriodStartEnd.startDate?.format("MM/DD/YY")} - ${comparisonPeriodStartEnd.endDate?.format("MM/DD/YY")}`
                : "Previous Period",
            color: `hsl(var(--chart-6))`,
        },
    }) satisfies ChartConfig;

const getHourlyChartConfig = (
    currentPeriodStartEnd: Range | null,
    comparisonPeriodStartEnd: Range | null,
) =>
    ({
        current: {
            label: currentPeriodStartEnd?.startDate?.format("MM/DD/YY"),
            color: `hsl(var(--chart-1))`,
        },
        previous: {
            label:
                comparisonPeriodStartEnd?.startDate?.format("MM/DD/YY") ||
                "Previous Period",
            color: `hsl(var(--chart-6))`,
        },
    }) satisfies ChartConfig;

const customerChartConfig = {
    total: {
        label: "Customer Breakdown",
        color: `hsl(var(--chart-1))`,
    },
} satisfies ChartConfig;

const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(value);

/** Tooltip that shows per-bar date ranges (e.g. "03/01/2025 - 03/31/2025")
 *  instead of the static full-period range from the chart config, so periods
 *  spanning multiple years display the correct range for each bar. */
const SalesTooltip: React.ComponentProps<typeof ChartTooltip>["content"] = ({
    active,
    payload,
    label,
}) => {
    if (!active || !payload?.length) return null;

    const rows = payload.map((item) => ({
        key: String(item.dataKey),
        color: item.color,
        range:
            item.dataKey === "current"
                ? item.payload.currentRange
                : item.payload.previousRange,
        value: formatCurrency((item.value as number) ?? 0),
    }));

    return (
        <div className="grid min-w-[8rem] gap-1.5 rounded-lg border border-neutral-200/50 bg-white px-2.5 py-1.5 text-xs shadow-xl dark:border-neutral-800/50 dark:bg-neutral-950">
            <div className="font-medium">{label}</div>
            {rows.map((row) => (
                <div key={row.key} className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: row.color }} />
                    <span className="text-neutral-500 dark:text-neutral-400">{row.range}</span>
                    <span className="ml-auto font-mono font-medium tabular-nums">{row.value}</span>
                </div>
            ))}
        </div>
    );
};

interface ClickableChartCardProps {
    title: string;
    description?: string;
    to?: string;
    children: React.ReactNode;
}

export function ClickableChartCard({
    title,
    description,
    to,
    children,
}: ClickableChartCardProps) {
    const content = (
        <>
            <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                    {title}
                </CardTitle>
                {description && (
                    <CardDescription>{description}</CardDescription>
                )}
            </CardHeader>
            <CardContent>{children}</CardContent>
        </>
    );

    if (to) {
        return (
            <Link to={to} className="block no-underline">
                <Card className="cursor-pointer transition-shadow duration-200 hover:shadow-md">
                    {content}
                </Card>
            </Link>
        );
    }

    return <Card>{content}</Card>;
}

const getXAxisLabel = (offset: string) => {
    if (offset === "0") return "";
    return `Days represented from ${offset}am to ${offset}am`;
};

export const Insights = () => {
    const {
        currentPeriod,
        comparisonPeriod,
        currentPeriodStartEnd,
        comparisonPeriodStartEnd,
        customHourOffset,
    } = useDashboardContext();

    // Select the appropriate data source
    const {
        report,
        isLoading: isSalesLoading,
        error: salesError,
        netSalesChange,
        ordersChange,
        currentAvgTicket,
        avgTicketChange,
        chartData,
    } = useQuerySalesBigquery();

    // Get hourly data from bigQuery
    const {
        hourlyData,
        isLoading: isHourlyLoading,
        error: hourlyError,
    } = useQuerySalesBigquery();

    const numUnreads = useSelector(getActiveStoreNumUnreads);

    const {
        customerData,
        isLoading: isCustomerLoading,
        error: customerError,
    } = useQueryCustomers();

    const { totals, changes } = useQueryCustomersSummary();

    const { subscriberCount, loading: smsLoading } = useQuerySMSSubscribers();

    // Get the dynamic chart config
    const salesChartConfig = getSalesChartConfig(
        currentPeriodStartEnd,
        comparisonPeriodStartEnd,
    );

    const hourlyChartConfig = getHourlyChartConfig(
        currentPeriodStartEnd,
        comparisonPeriodStartEnd,
    );

    const {
        breakdown,
        isLoading: isBreakdownLoading,
        error: breakdownError,
    } = useQuerySalesCohorts();

    return (
        <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-3">
                <MetricCard
                    title={t`Net Sales`}
                    value={formatCurrency(report?.netSales || 0)}
                    changeValue={netSalesChange}
                    icon={DollarSign}
                    isLoading={isSalesLoading}
                />
                <MetricCard
                    title={t`Orders`}
                    value={report?.count?.toLocaleString() ?? "0"}
                    changeValue={ordersChange}
                    icon={ShoppingBag}
                    isLoading={isSalesLoading}
                />
                <MetricCard
                    title={t`Average Ticket`}
                    value={formatCurrency(currentAvgTicket)}
                    changeValue={avgTicketChange}
                    icon={Receipt}
                    isLoading={isSalesLoading}
                />
            </div>
            <div className="grid gap-4 md:grid-cols-3">
                <Card className="md:col-span-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="space-y-1.5">
                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                    <Trans>Net Sales</Trans>
                                </CardTitle>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="h-3 w-3 rounded-full"
                                            style={{
                                                background: `hsl(var(--chart-1))`,
                                            }}
                                        />
                                        <span>
                                            {salesChartConfig.current.label}
                                        </span>
                                    </div>
                                    {comparisonPeriod !==
                                        ComparisonPeriod.NONE && (
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="h-3 w-3 rounded-full"
                                                style={{
                                                    background: `hsl(var(--chart-6))`,
                                                }}
                                            />
                                            <span>
                                                {
                                                    salesChartConfig.previous
                                                        .label
                                                }
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <DashboardGranularityPicker />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {isSalesLoading ? (
                            <Skeleton className="h-[330px] w-full" />
                        ) : (
                            <ChartContainer config={salesChartConfig}>
                                <BarChart
                                    data={chartData}
                                    height={300}
                                    margin={{
                                        top: 20,
                                        right: 12,
                                        left: 12,
                                        bottom: 20,
                                    }}
                                >
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="period"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        label={{ 
                                            value: getXAxisLabel(customHourOffset), 
                                            position: "bottom",
                                            offset: 10
                                        }}
                                    />
                                    <YAxis
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        tickFormatter={(value) => `$${value}`}
                                    />
                                    <ChartTooltip
                                        cursor={false}
                                        content={SalesTooltip}
                                    />
                                    {comparisonPeriod !==
                                        ComparisonPeriod.NONE && (
                                        <Bar
                                            dataKey="previous"
                                            fill="hsl(var(--chart-6))"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    )}
                                    <Bar
                                        dataKey="current"
                                        fill="hsl(var(--chart-1))"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ChartContainer>
                        )}
                    </CardContent>
                </Card>

                <div className="min-h-[400px] sm:min-h-0 md:col-span-1">
                    <QuickShortcutsCard />
                </div>
            </div>

            <SectionHeader title={t`Customers`} className="mt-6" />
            <div className="grid gap-4 md:grid-cols-4">
                <MetricCard
                    title={t`New Customers`}
                    value={totals.new.toLocaleString()}
                    changeValue={changes.newCustomersChange}
                    icon={Users}
                    isLoading={isCustomerLoading}
                />
                <MetricCard
                    title={t`Repeat Customers`}
                    value={totals.repeat.toLocaleString()}
                    changeValue={changes.repeatCustomersChange}
                    icon={Repeat}
                    isLoading={isCustomerLoading}
                />
                <MetricCard
                    title={t`App Customers`}
                    value={totals.app.toLocaleString()}
                    changeValue={changes.appCustomersChange}
                    icon={Smartphone}
                    isLoading={isCustomerLoading}
                />
                <MetricCard
                    title={t`SMS Subscribers`}
                    value={subscriberCount.toLocaleString()}
                    icon={MessageCircle}
                    isLoading={smsLoading}
                    to={Routes.GuestbookCampaignsSMS}
                />
            </div>
            <div className="grid gap-4 md:grid-cols-3">
                <div className="md:col-span-2 h-full">
                    <CustomerInsightsMonthlyChart />
                </div>
                <div className="h-full">
                    <ChartCard
                        title={t`Top Customers`}
                        description={
                            currentPeriodStartEnd?.startDate?.format("MM/DD/YY") && currentPeriodStartEnd?.endDate?.format("MM/DD/YY")
                                ? `${currentPeriodStartEnd.startDate.format("MM/DD/YY")} - ${currentPeriodStartEnd.endDate.format("MM/DD/YY")}`
                                : undefined
                        }
                        to={Routes.ReportsCustomerDirectoryInsights}
                        isLoading={isCustomerLoading}
                        className="h-full "
                        contentClassName="h-full"
                    >
                        <ResponsiveContainer width="100%" height="80%">
                            <ChartContainer config={customerChartConfig}>
                                <BarChart
                                    data={customerData}
                                    layout="vertical"
                                    margin={{
                                        right: 10,
                                        left: 10,
                                    }}
                                >
                                    <CartesianGrid horizontal={false} />
                                    <XAxis
                                        type="number"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        tickFormatter={(value) =>
                                            `$${value.toLocaleString()}`
                                        }
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                    />
                                    <ChartTooltip
                                        cursor={false}
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length)
                                                return null;
                                            const data = payload[0].payload;
                                            return (
                                                <div className="space-y-2 rounded-lg border bg-popover p-2 shadow-md">
                                                    <div className="font-medium">
                                                        {data.name}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                        ${data.value.toFixed(2)}{" "}
                                                        spent
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                        {data.orders} orders
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                        {customerData.map((_, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={`hsl(var(--chart-${(index % 5) + 1}))`}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ChartContainer>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </div>
            <div className="hidden">
                <LoyaltyTiers />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-6 rounded-lg border p-4 gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 w-full sm:w-auto">
                    <div className="flex items-center gap-3">
                        <Inbox className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <div className="text-sm font-medium">
                                <Trans>Unread</Trans>
                            </div>
                            <div className="text-xl font-bold">
                                {numUnreads.toString()}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <ThumbsUp className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <div className="text-sm font-medium">
                                <Trans>Positive</Trans>
                            </div>
                            <div className="text-xl font-bold">
                                {breakdown.feedbackData
                                    .find((item) => item.name === "Good")
                                    ?.value.toLocaleString() ?? "0"}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <ThumbsDown className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <div className="text-sm font-medium">
                                <Trans>Not Great</Trans>
                            </div>
                            <div className="text-xl font-bold">
                                {breakdown.feedbackData
                                    .find((item) => item.name === "Bad")
                                    ?.value.toLocaleString() ?? "0"}
                            </div>
                        </div>
                    </div>
                </div>

                <Link
                    to={Routes.GuestbookConversations}
                    className="no-underline text-card-foreground w-full sm:w-auto"
                >
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                    >
                        <Trans>View All Feedback</Trans>
                        <ChevronRightIcon className="ml-2 h-4 w-4" />
                    </Button>
                </Link>
            </div>
            <SectionHeader title={t`Insights`} className="mt-6" />
            <div className="mt-4 grid gap-4 md:grid-cols-2">
                <ChartCard
                    title={t`Sales by Hour (Totals)`}
                    isLoading={isHourlyLoading}
                >
                    <ChartContainer config={hourlyChartConfig}>
                        <LineChart
                            data={hourlyData}
                            margin={{
                                top: 20,
                                right: 12,
                                left: 12,
                            }}
                        >
                            <CartesianGrid vertical={false} />
                            <XAxis
                                dataKey="time"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                tickFormatter={(value) => `$${value}`}
                            />
                            <ChartTooltip
                                cursor={false}
                                content={
                                    <ChartTooltipContent
                                        indicator="line"
                                        valueIsCurrency
                                    />
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey="current"
                                stroke="var(--color-current)"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 6 }}
                            />
                            {comparisonPeriod !== ComparisonPeriod.NONE && (
                                <Line
                                    type="monotone"
                                    dataKey="previous"
                                    stroke="var(--color-previous)"
                                    strokeWidth={2}
                                    dot={false}
                                />
                            )}
                        </LineChart>
                    </ChartContainer>
                </ChartCard>
                <TopItemsChart />
                <SalesBreakdownCharts
                    breakdown={breakdown}
                    isLoading={isBreakdownLoading}
                />
                <TopPromotionsChart />
            </div>
        </div>
    );
};
